<template>
  <div class="pagamentos-lista">
    <v-card flat v-if="pagamentos.length < 1" class="text-xs-center pt-5 pb-5 px-4 empty-state-card">
      <v-icon size="42" color="grey lighten-1">account_balance_wallet</v-icon>
      <div class="subheading grey--text text--darken-1 mt-3">Nenhum pagamento registrado para este imóvel.</div>
      <div class="caption grey--text mt-1">Solicite o primeiro pagamento para iniciar o histórico.</div>
      <div class="mt-4" v-if="$hasGranularPermission('pagamentos', 'add')">
        <v-btn outline large class="secondary secondary--text mx-0" @click.stop="openDialogAdicionar">Solicitar Pagamento</v-btn>
      </div>
    </v-card>

    <template v-else>
      <v-card flat class="mb-3 resumo-card">
        <v-card-text class="py-3 px-4">
          <v-layout row wrap>
            <v-flex xs12 sm4 class="resumo-coluna">
              <div class="caption grey--text text--darken-1">Solicitações</div>
              <div class="subheading"><strong>{{ pagamentos.length }}</strong></div>
            </v-flex>
            <v-flex xs12 sm4 class="resumo-coluna">
              <div class="caption grey--text text--darken-1">Total solicitado</div>
              <div class="subheading secondary--text"><strong>{{ formatCurrency(totalSolicitado) }}</strong></div>
            </v-flex>
            <v-flex xs12 sm4 class="resumo-coluna">
              <div class="caption grey--text text--darken-1">Total pago</div>
              <div class="subheading verde--text"><strong>{{ formatCurrency(totalPago) }}</strong></div>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>

      <v-card flat v-for="(item, index) in pagamentos" :key="item.id" class="mb-2 pagamento-card" :class="`status-${item.status}`">
        <v-card-text class="py-4 px-4 lista_pagamentos">
          <v-layout row wrap align-center>
            <v-flex xs12 sm6 md7 class="mb-3 mb-sm-0 dados-coluna">
              <div class="subheading pagamento-titulo">
                <span class="status_circulo" :style="{ 'background-color': getStatusColor(item.status) }"></span>
                <strong>{{ item.descricao }}</strong>
              </div>

              <div class="mt-2">
                <span class="status_tag" :style="{ 'background-color': getStatusColor(item.status) }">
                  {{ formatStatus(item.status) }}
                </span>
                <span v-if="item.status === 'rejeitado' && item.observacao" class="caption grey--text text--darken-1 ml-2">
                  {{ item.observacao }}
                </span>
              </div>

              <v-layout row wrap class="mt-2">
                <v-flex xs12 md6 class="caption grey--text text--darken-1 meta-item">
                  <strong>Solicitado por:</strong> {{ item.solicitante ? item.solicitante.name : '---' }}
                </v-flex>
                <v-flex xs12 md6 class="caption grey--text text--darken-1 meta-item">
                  <strong>Aprovador:</strong> {{ item.aprovador ? item.aprovador.name : '---' }}
                </v-flex>
                <v-flex xs12 md6 class="caption grey--text text--darken-1 meta-item">
                  <strong>Vencimento:</strong> {{ formatDate(item.data_vencimento) }}
                </v-flex>
                <v-flex xs12 class="caption verde--text text--darken-2 meta-item" v-if="item.data_pagamento">
                  <strong>Pago em:</strong> {{ formatDate(item.data_pagamento) }}
                </v-flex>
              </v-layout>
            </v-flex>

            <v-flex xs12 sm4 md3 class="mb-3 mb-sm-0 valor-coluna">
              <div class="caption grey--text text--darken-1"><strong>Valor</strong></div>
              <div class="headline secondary--text valor-principal">{{ formatCurrency(item.valor) }}</div>
            </v-flex>

            <v-flex xs12 sm2 md2 class="acoes-coluna">
              <v-layout row align-center class="acoes-layout">
                <v-btn icon outline color="grey" class="mr-1" v-if="item.documento_url" :href="item.documento_url" target="_blank" title="Boleto">
                  <v-icon size="19">description</v-icon>
                </v-btn>
                <v-btn icon outline color="grey" class="mr-1" v-if="item.comprovante_url" :href="item.comprovante_url" target="_blank" title="Comprovante">
                  <v-icon size="19">receipt</v-icon>
                </v-btn>

                <v-menu bottom left offset-y v-if="hasMenuActions(item)">
                  <v-btn dark fab class="botao_lapis secondary ma-0" slot="activator">
                    <v-icon>more_vert</v-icon>
                  </v-btn>
                  <v-list dense>
                    <v-list-tile v-if="item.status === 'pendente' && canApproveAction(item)" @click="openDialogAprovar(item)">
                      <v-list-tile-action><v-icon color="blue">check_circle</v-icon></v-list-tile-action>
                      <v-list-tile-title>Avaliar</v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile v-if="canRegisterPaymentAction(item)" @click="openDialogPagar(item)">
                      <v-list-tile-action><v-icon color="green">payment</v-icon></v-list-tile-action>
                      <v-list-tile-title>Registrar Pagamento</v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile v-if="canDeleteAction(item)" @click="excluirPagamento(item)">
                      <v-list-tile-action><v-icon color="red">delete</v-icon></v-list-tile-action>
                      <v-list-tile-title class="red--text font-weight-bold">Apagar</v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider v-if="index + 1 < pagamentos.length"></v-divider>
      </v-card>

      <div class="text-xs-center mt-4 mb-4" v-if="$hasGranularPermission('pagamentos', 'add')">
        <v-btn outline large class="secondary secondary--text" @click.stop="openDialogAdicionar">Solicitar Novo Pagamento</v-btn>
      </div>
    </template>

    <PagamentosAdicionar
      :visible="dialogAdicionar"
      :imovelId="imovelId"
      @close="dialogAdicionar = false"
      @saved="loadPagamentos"
    />

    <PagamentosAprovar
      :visible="dialogAprovar"
      :pagamento="selectedPagamento"
      @close="dialogAprovar = false"
      @saved="loadPagamentos"
    />

    <PagamentosPagar
      :visible="dialogPagar"
      :pagamento="selectedPagamento"
      @close="dialogPagar = false"
      @saved="loadPagamentos"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { apiURL } from '@/config'
import { formatBRDate, formatBRLCurrency, normalizeMoney } from '@/utils/formatters'
import PagamentosAdicionar from './PagamentosAdicionar'
import PagamentosAprovar from './PagamentosAprovar'
import PagamentosPagar from './PagamentosPagar'

export default {
  props: ['imovelId'],
  components: { PagamentosAdicionar, PagamentosAprovar, PagamentosPagar },
  data () {
    return {
      pagamentos: [],
      dialogAdicionar: false,
      dialogAprovar: false,
      dialogPagar: false,
      selectedPagamento: null,
      colors: {
        pendente: '#EDC109',
        autorizado: '#006AB6',
        rejeitado: '#ED1B09',
        pago: '#4FBF46'
      },
      statusLabels: {
        pendente: 'Pendente',
        autorizado: 'Autorizado',
        rejeitado: 'Rejeitado',
        pago: 'Pago'
      }
    }
  },
  mounted () {
    this.loadPagamentos()
  },
  computed: {
    totalSolicitado () {
      return this.pagamentos.reduce((total, item) => total + normalizeMoney(item.valor), 0)
    },
    totalPago () {
      return this.pagamentos
        .filter(item => item.status === 'pago')
        .reduce((total, item) => total + normalizeMoney(item.valor), 0)
    }
  },
  methods: {
    loadPagamentos () {
      axios.get(`${apiURL}/pagamentos?imovel_id=${this.imovelId}`).then(response => {
        this.pagamentos = response.data.data
      }).catch(() => {
        this.pagamentos = []
      })
    },
    formatCurrency: formatBRLCurrency,
    formatDate: formatBRDate,
    formatStatus (status) {
      return this.statusLabels[status] || 'Não definido'
    },
    getStatusColor (status) {
      return this.colors[status] || 'grey'
    },
    normalizeId (value) {
      if (value === null || value === undefined || value === '') return null
      return String(value).trim()
    },
    normalizeEmail (value) {
      if (!value) return null
      return String(value).trim().toLowerCase()
    },
    isCurrentUser (targetId, targetUser) {
      const user = this.$auth.user()
      if (!user) return false

      const currentId = this.normalizeId(user.id)
      const currentEmail = this.normalizeEmail(user.email)
      const directId = this.normalizeId(targetId)
      const relationId = this.normalizeId(targetUser && targetUser.id)
      const relationEmail = this.normalizeEmail(targetUser && targetUser.email)

      const idMatch = !!currentId && ((directId && currentId === directId) || (relationId && currentId === relationId))
      const emailMatch = !!currentEmail && !!relationEmail && currentEmail === relationEmail

      return idMatch || emailMatch
    },
    podeAprovar (item) {
      const isAdmin = this.$auth.check('Administrador')
      return this.isCurrentUser(item.aprovador_id, item.aprovador) || isAdmin
    },
    canApproveAction (item) {
      if (!this.$hasGranularPermission('pagamentos', 'edit')) return false
      // Approval is restricted to the assigned approver.
      return this.isCurrentUser(item.aprovador_id, item.aprovador)
    },
    canRegisterPaymentAction (item) {
      if (!this.$hasGranularPermission('pagamentos', 'edit')) return false
      if (item.status !== 'autorizado') return false
      return this.isCurrentUser(item.solicitante_id, item.solicitante)
    },
    canDeleteAction (item) {
      if (!this.$hasGranularPermission('pagamentos', 'delete')) return false
      const isOwner = this.isCurrentUser(item.solicitante_id, item.solicitante)
      return item.status !== 'pago' && (this.podeAprovar(item) || isOwner)
    },
    hasMenuActions (item) {
      const canEvaluate = item.status === 'pendente' && this.canApproveAction(item)
      const canRegister = this.canRegisterPaymentAction(item)
      const canDelete = this.canDeleteAction(item)

      return canEvaluate || canRegister || canDelete
    },
    openDialogAprovar (item) {
      this.selectedPagamento = item
      this.dialogAprovar = true
    },
    openDialogAdicionar () {
      // Open after current click cycle to avoid immediate outside-click close.
      setTimeout(() => {
        this.dialogAdicionar = true
      }, 0)
    },
    openDialogPagar (item) {
      this.selectedPagamento = item
      this.dialogPagar = true
    },
    excluirPagamento (item) {
      if (confirm('Tem certeza que deseja excluir esta solicitação?')) {
        axios.delete(`${apiURL}/pagamentos/${item.id}`).then(() => {
          this.loadPagamentos()
          this.$store.dispatch('utils/showSnackbar', {
            type: 'success',
            text: 'Solicitação excluída com sucesso!'
          })
        }).catch(() => {
          this.$store.dispatch('utils/showSnackbar', {
            type: 'error',
            text: 'Erro ao excluir solicitação.'
          })
        })
      }
    }
  }
}
</script>

<style scoped>
.empty-state-card {
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
}

.resumo-card {
  border: 1px solid #ececec;
  border-radius: 8px;
  background: #fafafa;
}

.resumo-coluna {
  margin-bottom: 8px;
}

.pagamento-card {
  border: 1px solid #ececec;
  border-left: 4px solid #d9d9d9;
  border-radius: 8px;
  transition: box-shadow 0.2s ease;
}

.pagamento-card:hover {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.pagamento-card.status-pendente {
  border-left-color: #EDC109;
}

.pagamento-card.status-autorizado {
  border-left-color: #006AB6;
}

.pagamento-card.status-rejeitado {
  border-left-color: #ED1B09;
}

.pagamento-card.status-pago {
  border-left-color: #4FBF46;
}

.pagamento-titulo {
  line-height: 1.3;
}

.status_circulo {
  display: inline-block;
  height: 12px;
  width: 12px;
  border-radius: 100%;
  vertical-align: middle;
  margin-right: 6px;
  margin-top: -3px;
}

.status_tag {
  color: #fff;
  display: inline-block;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 3px 10px;
}

.meta-item {
  margin-bottom: 3px;
}

.valor-principal {
  line-height: 1.2;
  word-break: break-word;
}

.botao_lapis {
  box-shadow: none;
  width: 47px;
  height: 47px;
}

@media (max-width: 599px) {
  .dados-coluna {
    margin-bottom: 10px;
  }

  .valor-coluna {
    margin-bottom: 8px;
  }

  .acoes-coluna {
    margin-top: 4px;
  }

  .acoes-layout {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .botao_lapis {
    width: 42px;
    height: 42px;
  }
}

@media (min-width: 600px) {
  .resumo-coluna {
    margin-bottom: 0;
  }

  .acoes-layout {
    justify-content: flex-end;
    flex-wrap: nowrap;
  }
}
</style>



// WEBPACK FOOTER //
// PagamentosLista.vue?7832b97b